import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Button, Empty, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, SyncOutlined } from '@ant-design/icons';

import type { CfdiCatalogoConteoPorMes, SolicitudDetalle } from '../api';

const { Text } = Typography;

// Mismos labels/colores que ESTADO_TAG en CargaArchivos.tsx (no se comparte
// el módulo entre páginas para no acoplar una página a otra; es un mapa
// chico y estable, el riesgo de que se desincronicen es bajo).
const ESTADO_TAG: Record<string, { color: string; label: string }> = {
  pendiente:             { color: 'default',    label: 'Pendiente' },
  en_proceso:            { color: 'processing', label: 'En proceso' },
  procesando:            { color: 'processing', label: 'Procesando' },
  completada:            { color: 'success',    label: 'Completada' },
  completada_sin_datos:  { color: 'success',    label: 'Sin CFDIs en el rango' },
  error:                 { color: 'error',       label: 'Error' },
  cancelada:             { color: 'default',    label: 'Cancelada' },
};

const DIRECCION_LABEL: Record<string, string> = {
  emitidos:  'Emitidos',
  recibidos: 'Recibidos',
};

/** Una fila por mes calendario × dirección (emitidos/recibidos). El Estado y
 * el Detalle salen de las sub-solicitudes (única fuente para eso); el
 * conteo de CFDIs sale de `conteoPorMes` (catálogo real por fecha_emision),
 * no de numero_cfdis/total_xmls de la solicitud — una ventana puede cruzar
 * un mes y de ahí salía el "332 en Enero y 332 en Febrero" duplicado. */
interface FilaMes {
  key:          string;
  mes:          string; // 'YYYY-MM', para ordenar
  mesLabel:     string;
  direccion:    string;
  estados:      string[];
  ids:          { id: string; estado: string }[]; // por sub-solicitud — Cancelar solo debe tocar las cancelables, no una 'completada' que comparte fila con una 'error'
  numeroCfdis:  number;
  totalXmls:    number;   // XMLs reales subidos (suma de total_xmls de las sub-solicitudes de la fila)
  esParcial:    boolean;  // alguna sub-solicitud de la fila quedó parcial (total_xmls < numero_cfdis)
  mensajeError: string | null;
}

function agruparPorMes(solicitudes: SolicitudDetalle[], conteoPorMes: CfdiCatalogoConteoPorMes[]): FilaMes[] {
  const conteoReal = new Map<string, number>();
  for (const c of conteoPorMes) conteoReal.set(`${c.mes}_${c.direccion}`, c.total);
  // Solo se descarta un mes cuando el catálogo SÍ respondió con conteos
  // reales. Si viene vacío (el fetch falló o el RFC todavía no tiene
  // catálogo), no hay información para decidir: se prefiere mostrar el mes
  // de margen antes que ocultar meses legítimos por falta de datos.
  const hayConteoReal = conteoPorMes.length > 0;

  const filas = new Map<string, FilaMes>();

  for (const s of solicitudes) {
    if (!s.fecha_inicio || !s.fecha_fin) continue;
    const inicio = dayjs(s.fecha_inicio);
    const fin = dayjs(s.fecha_fin);
    if (!inicio.isValid() || !fin.isValid()) continue;

    // Cada mes calendario que la ventana toca, aunque sea parcialmente —
    // salvo el mes de "margen" (ej. pedir hasta el 01 del mes siguiente para
    // esquivar el límite de duplicados del SAT) cuando no tiene CFDIs
    // reales: no tiene sentido mostrarle al cliente un mes que no se pidió
    // de verdad, solo quedó rozado por el rango.
    const mesInicio = inicio.format('YYYY-MM');
    let cursor = inicio.startOf('month');
    const finMes = fin.startOf('month');
    while (cursor.isSame(finMes) || cursor.isBefore(finMes)) {
      const mes = cursor.format('YYYY-MM');
      const key = `${mes}_${s.tipo}`;
      if (hayConteoReal && mes !== mesInicio && (conteoReal.get(key) ?? 0) === 0) {
        cursor = cursor.add(1, 'month');
        continue;
      }
      // No sumar CFDIs de filas canceladas (reintento comparte la misma key).
      const cuentaParaElTotal = s.estado !== 'cancelada';
      const cfdisDeEsteItem = cuentaParaElTotal ? Number(s.numero_cfdis ?? 0) : 0;
      const xmlsDeEsteItem = cuentaParaElTotal ? Number(s.total_xmls ?? 0) : 0;
      const esParcialDeEsteItem = cuentaParaElTotal && (s.parcial ?? false);
      const existente = filas.get(key);
      if (existente) {
        existente.estados.push(s.estado);
        existente.ids.push({ id: s.id, estado: s.estado });
        existente.numeroCfdis += cfdisDeEsteItem;
        existente.totalXmls += xmlsDeEsteItem;
        if (esParcialDeEsteItem) existente.esParcial = true;
        if (!existente.mensajeError && s.estado === 'error') {
          existente.mensajeError = s.mensaje_error || s.mensaje || null;
        }
      } else {
        filas.set(key, {
          key,
          mes,
          mesLabel: cursor.format('MMMM YYYY'),
          direccion: s.tipo,
          estados: [s.estado],
          ids: [{ id: s.id, estado: s.estado }],
          numeroCfdis: cfdisDeEsteItem,
          totalXmls: xmlsDeEsteItem,
          esParcial: esParcialDeEsteItem,
          mensajeError: s.estado === 'error' ? (s.mensaje_error || s.mensaje || null) : null,
        });
      }
      cursor = cursor.add(1, 'month');
    }
  }

  return Array.from(filas.values()).sort((a, b) =>
    a.mes === b.mes ? a.direccion.localeCompare(b.direccion) : b.mes.localeCompare(a.mes));
}

// Si el mes tiene más de una sub-solicitud (ej. una ventana larga trocea a
// mitad de mes), se muestra el peor estado: error > en curso > completada.
const PRIORIDAD_ESTADO: Record<string, number> = {
  error: 3,
  en_proceso: 2,
  procesando: 2,
  pendiente: 2,
  completada: 1,
  completada_sin_datos: 1,
  cancelada: 0, // una 'completada' en la misma fila siempre debe ganarle a una cancelada vieja
};

function estadoRepresentativo(estados: string[]): string {
  return estados.reduce((peor, actual) =>
    (PRIORIDAD_ESTADO[actual] ?? 0) > (PRIORIDAD_ESTADO[peor] ?? 0) ? actual : peor);
}

// Completada también es cancelable: libera el rango para re-Solicitarlo
// cuando una descarga vieja quedó corta (ej. bug de FechaFinal ya corregido).
const ESTADOS_CANCELABLES = new Set(['pendiente', 'en_proceso', 'procesando', 'error', 'completada', 'completada_sin_datos']);

interface Props {
  solicitudes: SolicitudDetalle[];
  /** Conteo real de CFDIs por mes/dirección, de GET /cfdi-catalogo/{rfc}. */
  conteoPorMes?: CfdiCatalogoConteoPorMes[];
  loading?: boolean;
  /** Cancela (deja de rastrear localmente) las sub-solicitudes de una fila —
   * ver botón "Cancelar" más abajo. Este componente no llama a la API
   * directamente, solo delega en el caller (mismo criterio que el resto de
   * los componentes de src/components/, que no conocen la capa de API). */
  onCancelar?: (ids: string[]) => Promise<void>;
}

/** Foto de qué meses ya se pidieron al SAT y cómo quedó cada uno —
 * agrupando las sub-solicitudes (ventana de fechas × emitidos/recibidos,
 * ver GET /solicitudes/{rfc}) por mes calendario. El reintento puntual de
 * una ventana en error ya existe en SolicitudesDetalleTable
 * (CargaArchivos.tsx) y no se duplica acá a propósito — pero si el usuario
 * quiere dejar de rastrear una fila trabada/en error para poder pedirla de
 * nuevo desde el date-picker de arriba, "Cancelar" cubre eso sin necesidad
 * de operar la fila (ver onCancelar). */
export function SolicitudesEstadoPorMes({ solicitudes, conteoPorMes, loading, onCancelar }: Props) {
  const filas = useMemo(() => agruparPorMes(solicitudes, conteoPorMes ?? []), [solicitudes, conteoPorMes]);
  const [keysCancelando, setKeysCancelando] = useState<Set<string>>(new Set());

  // Misma key que FilaMes (`${mes}_${direccion}`) para lookup directo por fila.
  const conteoReal = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of conteoPorMes ?? []) map.set(`${c.mes}_${c.direccion}`, c.total);
    return map;
  }, [conteoPorMes]);

  // Resumen global del RFC — solo cuenta las sub-solicitudes 'completada'
  // (excluye en_proceso/error/pendiente para no mostrar un "X de Y" engañoso)
  // y cuenta las ventanas parciales/en error para advertir que NO está completa.
  const resumen = useMemo(() => {
    let totalXmls = 0;
    let totalCfdis = 0;
    let parciales = 0;
    let errores = 0;
    for (const s of solicitudes) {
      // Legacy sin fecha_inicio/fecha_fin: agruparPorMes también las salta.
      if (!s.fecha_inicio || !s.fecha_fin) continue;
      if (s.estado === 'completada') {
        totalXmls += Number(s.total_xmls ?? 0);
        totalCfdis += Number(s.numero_cfdis ?? 0);
        if (s.parcial) parciales += 1;
      } else if (s.estado === 'error') {
        errores += 1;
      }
    }
    return { totalXmls, totalCfdis, parciales, errores };
  }, [solicitudes]);

  async function handleCancelar(fila: FilaMes) {
    if (!onCancelar) return;
    // Solo las cancelables — la fila puede mezclar una 'completada' real con una 'error' de un reintento.
    const idsCancelables = fila.ids.filter(x => ESTADOS_CANCELABLES.has(x.estado)).map(x => x.id);
    if (idsCancelables.length === 0) return;
    setKeysCancelando(prev => new Set(prev).add(fila.key));
    try {
      await onCancelar(idsCancelables);
    } finally {
      setKeysCancelando(prev => { const next = new Set(prev); next.delete(fila.key); return next; });
    }
  }

  const columns: ColumnsType<FilaMes> = [
    {
      title: 'Mes',
      key: 'mes',
      width: 160,
      render: (_, f) => <Text style={{ textTransform: 'capitalize' }}>{f.mesLabel}</Text>,
    },
    {
      title: 'Dirección',
      key: 'direccion',
      width: 110,
      render: (_, f) => (
        <Tag color={f.direccion === 'emitidos' ? 'blue' : 'purple'}>
          {DIRECCION_LABEL[f.direccion] ?? f.direccion}
        </Tag>
      ),
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 160,
      render: (_, f) => {
        const rep = estadoRepresentativo(f.estados);
        const info = ESTADO_TAG[rep] ?? { color: 'default', label: rep };
        const enCurso = rep === 'en_proceso' || rep === 'procesando' || rep === 'pendiente';
        return <Tag color={info.color} icon={enCurso ? <SyncOutlined spin /> : undefined}>{info.label}</Tag>;
      },
    },
    {
      title: 'CFDIs descargados',
      key: 'numeroCfdis',
      width: 170,
      align: 'right',
      render: (_, f) => (
        <Space size={6}>
          <Text>{`${conteoReal.get(f.key) ?? f.totalXmls} CFDI(s)`}</Text>
          {f.esParcial && (
            <Tooltip title="Hay una solicitud que subió menos XMLs de los que declaró el SAT — puede faltar completar esta ventana.">
              <Tag color="warning" style={{ marginInlineEnd: 0 }}>Parcial</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Detalle',
      key: 'detalle',
      width: 320,
      render: (_, f) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{f.mensajeError || '—'}</div>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 110,
      render: (_, f) => {
        if (!onCancelar || !ESTADOS_CANCELABLES.has(estadoRepresentativo(f.estados))) return null;
        return (
          <Popconfirm
            title="Cancelar seguimiento"
            description="Esto no cancela nada en el SAT (su servicio no lo permite) — solo deja de rastrear estas sub-solicitudes aquí, para poder volver a pedir este período."
            okText="Cancelar"
            cancelText="Volver"
            onConfirm={() => handleCancelar(f)}
          >
            <Button danger size="small" icon={<DeleteOutlined />} loading={keysCancelando.has(f.key)}>
              Cancelar
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <>
      {(resumen.totalCfdis > 0 || resumen.parciales > 0 || resumen.errores > 0) && (
        <Alert
          type={resumen.parciales > 0 || resumen.errores > 0 ? 'warning' : 'success'}
          showIcon
          style={{ marginBottom: 12 }}
          message={`${resumen.totalXmls} de ${resumen.totalCfdis} comprobantes descargados`}
          description={
            resumen.parciales > 0 || resumen.errores > 0
              ? `${resumen.parciales} ventana(s) con descarga parcial y ${resumen.errores} en error — la descarga del SAT para este RFC NO está completa.`
              : 'La descarga del SAT para este RFC está completa.'
          }
        />
      )}
      <Table<FilaMes>
        rowKey="key"
        columns={columns}
        dataSource={filas}
        loading={loading}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        size="small"
        locale={{ emptyText: <Empty description="Todavía no se pidió ninguna descarga al SAT." style={{ padding: 16 }} /> }}
      />
    </>
  );
}
